import React, { useState } from 'react';
import { FileText, AlertTriangle, CheckCircle, Clock, Shield, Target, TrendingUp, ChevronDown, Scale } from 'lucide-react';
import CTASection from '../../components/CTASection';

const OfficeActionsPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);

  const officeActionTypes = [
    {
      type: 'Substantive Refusal',
      severity: 'High',
      description: 'Examiner believes your mark is likely to cause confusion with existing trademark',
      commonReasons: ['Likelihood of confusion', 'Descriptiveness', 'Generic terms', 'Merely ornamental'],
      responseComplexity: 'Complex - Requires legal arguments',
      successRate: '60-70% with proper response'
    },
    {
      type: 'Technical Refusal',
      severity: 'Medium',
      description: 'Issues with application format, specimens, or descriptions',
      commonReasons: ['Improper specimens', 'Unclear descriptions', 'Wrong class selection', 'Insufficient information'],
      responseComplexity: 'Moderate - Often fixable with amendments',
      successRate: '85-95% with corrections'
    },
    {
      type: 'Section 2(d) Refusal',
      severity: 'Very High',
      description: 'Most common and serious - likelihood of confusion with prior mark',
      commonReasons: ['Similar mark and goods', 'Related industries', 'Similar sound/appearance', 'Similar meaning'],
      responseComplexity: 'Very Complex - May require consent agreements',
      successRate: '40-60% depending on similarity'
    },
    {
      type: 'Section 2(e) Refusal',
      severity: 'High',
      description: 'Mark considered merely descriptive or deceptive',
      commonReasons: ['Describes goods/services', 'Geographic description', 'Surname', 'Deceptive misdescription'],
      responseComplexity: 'Complex - Requires acquired distinctiveness proof',
      successRate: '50-70% with evidence of secondary meaning'
    }
  ];

  const responseStrategies = [
    {
      strategy: 'Legal Arguments',
      description: 'Craft compelling legal arguments distinguishing your mark',
      whenToUse: 'Substantive refusals, likelihood of confusion',
      requirements: 'Legal research, precedent cases, expert analysis',
      cost: '$1,500-$3,500',
      timeframe: '4-6 weeks to prepare'
    },
    {
      strategy: 'Evidence of Distinctiveness',
      description: 'Provide proof that your mark has acquired secondary meaning',
      whenToUse: 'Descriptiveness refusals, weak marks',
      requirements: 'Sales data, advertising spend, surveys, media coverage',
      cost: '$2,000-$5,000',
      timeframe: '6-8 weeks to gather and present'
    },
    {
      strategy: 'Amendment of Description',
      description: 'Modify goods/services description to avoid conflict',
      whenToUse: 'Technical issues, class problems',
      requirements: 'Strategic analysis of market position',
      cost: '$500-$1,500',
      timeframe: '2-3 weeks'
    },
    {
      strategy: 'Consent Agreement',
      description: 'Negotiate with prior mark owner for coexistence',
      whenToUse: 'Likelihood of confusion with cooperative parties',
      requirements: 'Negotiation skills, legal documentation',
      cost: '$2,500-$7,500',
      timeframe: '2-4 months'
    },
    {
      strategy: 'Abandonment of Prior Mark',
      description: 'Argue that cited mark is abandoned or not in use',
      whenToUse: 'Old marks, no evidence of current use',
      requirements: 'Investigation, evidence gathering',
      cost: '$1,500-$3,000',
      timeframe: '4-6 weeks'
    }
  ];

  const timeline = [
    { weeks: '0', milestone: 'Office Action Received', description: 'USPTO mails office action to applicant' },
    { weeks: '1-2', milestone: 'Review & Analysis', description: 'Attorney reviews refusal and develops strategy' },
    { weeks: '2-4', milestone: 'Evidence Gathering', description: 'Collect supporting materials, research precedents' },
    { weeks: '4-5', milestone: 'Response Drafting', description: 'Prepare comprehensive legal response' },
    { weeks: '5-6', milestone: 'Filing Response', description: 'Submit response to USPTO before 6-month deadline' },
    { weeks: '12-20', milestone: 'USPTO Review', description: 'Examiner reviews response and makes decision' },
    { weeks: '20+', milestone: 'Outcome', description: 'Approval or final refusal' }
  ];

  const statistics = [
    { number: '87%', label: 'Applications receive office actions', source: 'USPTO 2023 Report' },
    { number: '70%', label: 'Office actions overcome with proper response', source: 'Trademark Office Data' },
    { number: '6 months', label: 'Deadline to respond', source: 'USPTO Regulation' },
    { number: '$2,500', label: 'Average response cost', source: 'Industry Average' }
  ];

  const caseStudies = [
    {
      title: 'Tech Company Overcomes Likelihood of Confusion',
      challenge: 'USPTO cited 3 similar marks in software category',
      approach: 'Distinguished goods/services, emphasized different market segments, provided evidence of coexistence',
      evidence: '5 years of marketplace coexistence, different customer bases, different distribution channels',
      outcome: 'Approved after comprehensive legal argument',
      timeframe: '8 weeks for response, 4 months for approval',
      cost: '$3,200'
    },
    {
      title: 'Restaurant Chain Proves Acquired Distinctiveness',
      challenge: 'Mark deemed merely descriptive of restaurant services',
      approach: 'Submitted evidence of secondary meaning through extensive use',
      evidence: '$10M advertising spend over 5 years, 50 locations, media coverage, customer surveys',
      outcome: 'Approved on Supplemental Register, later moved to Principal',
      timeframe: '12 weeks for response, 6 months for approval',
      cost: '$4,800'
    },
    {
      title: 'E-commerce Brand Secures Consent Agreement',
      challenge: 'Similar mark owned by small regional business',
      approach: 'Negotiated coexistence agreement with prior mark owner',
      evidence: 'Geographic separation, different price points, different customer demographics',
      outcome: 'Both parties signed consent, trademark approved',
      timeframe: '3 months for negotiation, 2 months for approval',
      cost: '$5,500'
    }
  ];

  const commonMistakes = [
    { mistake: 'Missing the 6-month deadline', consequence: 'Application abandoned, must refile and pay new fees', howToAvoid: 'Set multiple calendar reminders, hire attorney immediately' },
    { mistake: 'Inadequate legal arguments', consequence: 'Final refusal, appeal required or abandon application', howToAvoid: 'Use experienced trademark attorney, not general lawyer' },
    { mistake: 'Insufficient evidence', consequence: 'Response rejected, must provide more proof or abandon', howToAvoid: 'Gather comprehensive evidence early in process' },
    { mistake: 'Wrong response strategy', consequence: 'Wasted time and money on ineffective approach', howToAvoid: 'Get expert analysis before responding' },
    { mistake: 'Trying to respond yourself', consequence: '90% DIY responses fail, leading to abandonment', howToAvoid: 'Always use trademark attorney for office actions' }
  ];

  const faqs = [
    {
      question: 'What happens if I don\'t respond to an office action?',
      answer: 'Failure to respond within 6 months results in automatic abandonment of your application. You lose all filing fees (minimum $350) and must start over with a new application. The 6-month deadline is strictly enforced with no extensions except in extraordinary circumstances like natural disasters or death.'
    },
    {
      question: 'Can I respond to an office action myself?',
      answer: 'While legally permitted, it\'s strongly discouraged. USPTO statistics show that 90% of self-represented applicants fail to overcome substantive office actions. Trademark law is complex, and examiners expect proper legal arguments with precedent citations. An attorney response costs $1,500-$3,500 but has a 70% success rate versus 10% for DIY responses.'
    },
    {
      question: 'How long do I have to respond?',
      answer: 'You have 6 months from the mailing date of the office action. This is a hard deadline with no automatic extensions. However, you can file an extension request for an additional $125 fee, giving you a total of 9 months. We recommend responding within 4-5 months to allow time for back-and-forth if needed.'
    },
    {
      question: 'What if my response is rejected?',
      answer: 'If the examiner maintains the refusal after your response, you receive a Final Office Action. At this point, your options are: (1) File a Request for Reconsideration with new evidence ($0 fee but rarely successful), (2) Appeal to the Trademark Trial and Appeal Board ($450 fee plus $3,000-$10,000 in legal costs), or (3) Abandon the application.'
    },
    {
      question: 'Can I amend my trademark to overcome a refusal?',
      answer: 'It depends on the refusal type. You can generally amend descriptions, disclaim descriptive elements, or delete certain goods/services. However, you cannot materially alter the mark itself (changing words, design elements, or overall commercial impression). Minor changes like removing punctuation may be allowed.'
    },
    {
      question: 'What is the success rate of office action responses?',
      answer: 'Success rates vary by refusal type and response quality: Technical refusals (incorrect specimens, descriptions) have 85-95% success rate. Descriptiveness refusals have 50-70% success rate with strong evidence. Likelihood of confusion refusals have 40-60% success rate. Overall, attorney-prepared responses succeed about 70% of the time versus 10% for self-represented applicants.'
    },
    {
      question: 'How much does it cost to respond to an office action?',
      answer: 'Costs vary by complexity: Simple technical fixes: $500-$1,500. Standard substantive refusals: $1,500-$3,500. Complex likelihood of confusion: $2,500-$5,000. Consent agreements: $3,000-$7,500. Multiple refusals or international issues: $5,000+. However, this is far less than the $100,000+ cost of rebranding if your application fails.'
    },
    {
      question: 'What happens after I submit my response?',
      answer: 'The USPTO examining attorney reviews your response, which typically takes 3-6 months. They may: (1) Approve your application and publish it for opposition, (2) Issue another office action requiring additional information, (3) Issue a Final Office Action maintaining the refusal, or (4) Request an examiner\'s amendment for minor clarifications. You\'ll receive notification via email and USPS mail.'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#FF6B35]/20 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Office Action Response Services
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Trademark <span className="text-[#FF6B35]">Office Actions</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Expert legal responses to USPTO office actions with 70% success rate. Don't let a refusal stop your trademark registration.
            </p>
            <div className="flex gap-4">
              <button className="bg-[#FF6B35] hover:bg-[#FF8C42] text-white font-bold px-8 py-4 rounded-lg transition-all">
                Get Expert Help Now
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                Free Case Review
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#FF6B35]" />
                70% Success Rate
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#FF6B35]" />
                6-Month Deadline
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#FF6B35]" />
                Expert Attorneys
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {statistics.map((stat, index) => (
              <div key={index}>
                <div className="text-4xl font-bold text-[#FF6B35] mb-2">{stat.number}</div>
                <div className="text-gray-700 mb-1">{stat.label}</div>
                <div className="text-xs text-gray-500">{stat.source}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What is Office Action */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                What is an Office Action?
              </div>
              <h2 className="text-4xl font-bold text-black mb-6">Understanding USPTO Office Actions</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                An office action is an official letter from a USPTO examining attorney explaining why your trademark application cannot be approved in its current form. It's not an outright rejection—it's an opportunity to address concerns and overcome obstacles.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                87% of trademark applications receive at least one office action. This is completely normal and doesn't mean your application will fail. With a proper legal response, 70% of office actions are successfully overcome.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Office actions can be substantive (legal issues with the mark itself) or procedural (technical problems with the application). Each type requires a different response strategy, and choosing the wrong approach can doom your application.
              </p>
              <p className="text-lg font-semibold text-black">
                You have 6 months to respond or your application is abandoned. Don't waste time—every day counts.
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-8 border-2 border-red-200">
              <AlertTriangle className="w-16 h-16 text-red-500 mb-6" />
              <h3 className="text-2xl font-bold text-black mb-4">Critical Deadlines</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-black">6 Months to Respond</div>
                    <div className="text-sm text-gray-700">Hard deadline from mailing date</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-black">Extension Available</div>
                    <div className="text-sm text-gray-700">$125 fee for 3 additional months</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-bold text-black">No Response = Abandonment</div>
                    <div className="text-sm text-gray-700">Lose all filing fees and must start over</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Office Action Types */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Types of Office Actions</h2>
            <p className="text-lg text-gray-600">Understanding what you're facing and how to respond</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {officeActionTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg border-l-4 border-[#FF6B35]">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-2xl font-bold text-black">{type.type}</h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    type.severity === 'Very High' ? 'bg-red-100 text-red-700' :
                    type.severity === 'High' ? 'bg-orange-100 text-orange-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>
                    {type.severity}
                  </span>
                </div>
                <p className="text-gray-700 mb-4">{type.description}</p>
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-600 mb-2">Common Reasons:</div>
                    <ul className="space-y-1">
                      {type.commonReasons.map((reason, idx) => (
                        <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-[#FF6B35]">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-600 mb-1">Response Complexity:</div>
                    <div className="text-sm text-gray-700">{type.responseComplexity}</div>
                  </div>
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-sm font-semibold text-[#FF6B35]">Success Rate: {type.successRate}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Response Strategies */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Response Strategies</h2>
            <p className="text-lg text-gray-600">Choosing the right approach is critical to success</p>
          </div>
          <div className="space-y-6">
            {responseStrategies.map((strategy, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-[#FF6B35] rounded-lg flex items-center justify-center">
                      <Target className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-black mb-2">{strategy.strategy}</h3>
                    <p className="text-gray-700 mb-4">{strategy.description}</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-semibold text-gray-600">When to Use:</span>
                        <p className="text-gray-700">{strategy.whenToUse}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-600">Requirements:</span>
                        <p className="text-gray-700">{strategy.requirements}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-600">Cost:</span>
                        <p className="text-[#FF6B35] font-bold">{strategy.cost}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-600">Timeline:</span>
                        <p className="text-gray-700">{strategy.timeframe}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Office Action Response Timeline</h2>
            <p className="text-lg text-gray-600">What to expect from receipt to resolution</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-[#FF6B35] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    Week {item.weeks}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-20 bg-[#FF6B35]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <h3 className="text-xl font-bold text-black mb-2">{item.milestone}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Success Stories</h2>
            <p className="text-lg text-gray-600">Real office action responses that succeeded</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {caseStudies.map((study, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-black mb-4">{study.title}</h3>
                <div className="space-y-4">
                  <div>
                    <div className="text-xs font-semibold text-red-500 uppercase mb-2">Challenge</div>
                    <p className="text-sm text-gray-700">{study.challenge}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-[#FF6B35] uppercase mb-2">Approach</div>
                    <p className="text-sm text-gray-700">{study.approach}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-blue-600 uppercase mb-2">Evidence</div>
                    <p className="text-sm text-gray-700">{study.evidence}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-green-600 uppercase mb-2">Outcome</div>
                    <p className="text-sm text-gray-700 font-semibold">{study.outcome}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-300">
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        <Clock className="w-4 h-4 inline text-gray-500 mr-1" />
                        <span className="text-gray-600">{study.timeframe}</span>
                      </div>
                      <div className="text-[#FF6B35] font-bold">{study.cost}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Mistakes */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Common Mistakes to Avoid</h2>
            <p className="text-lg text-gray-600">Don't let these errors doom your response</p>
          </div>
          <div className="space-y-6">
            {commonMistakes.map((mistake, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-red-500">
                <div className="flex gap-4">
                  <AlertTriangle className="w-8 h-8 text-red-500 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-black mb-2">{mistake.mistake}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-semibold text-red-600">Consequence:</span>
                        <p className="text-sm text-gray-700">{mistake.consequence}</p>
                      </div>
                      <div>
                        <span className="text-sm font-semibold text-green-600">How to Avoid:</span>
                        <p className="text-sm text-gray-700">{mistake.howToAvoid}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#FF6B35] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />
    </div>
  );
};

export default OfficeActionsPage;